import React, { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import styled from 'styled-components';
import { LayoutGrid, ArrowRight, Swords, Compass, Heart, Laugh, Rocket, Ghost, Zap, Sparkles, Film, Castle } from 'lucide-react';
import { Container } from '../../components/styled/Container';
import { PageLayout } from '../../components/styled/PageLayout';
import { regionFromPathname } from '../../utils/regionFromPath';
import { categoriesGenrePath } from '../../utils/categoriesPaths';
import { fetchPublicGenres } from '../../store/actions/genreActions';
import { useI18n } from '../../hooks/useI18n';
import StaticAdBanner from '../../components/ads/StaticAdBanner';
import AdInline from '../../components/ads/AdInline';
import { bannerAds, cardAds } from '../../data/staticAds';
import { useFetchManagedPlacements } from '../../hooks/useFetchManagedPlacements';
import { useSiteAdsManaged } from '../../hooks/useSiteAdsManaged';
import { trackAdClick, trackAdImpression } from '../../store/actions/adActions';
import { selectAdPlacementFirst } from '../../store/selectors/adSelectors';
import { managedPlacementToAd, pickStaticFallback } from '../../utils/adMapper';
import { selectAdsStaticEnabled } from '../../store/reducers/sitePublicReducer';
import { makeImpressionKey, shouldTrackImpression } from '../../utils/adTracking';
import PageLoading from '../../components/ui/PageLoading';

const CATEGORIES_AD_REQ = Object.freeze([
  { placement: 'inline-banner', limit: 1 },
  { placement: 'results-inline', limit: 1 },
]);

const Header = styled(motion.div)`
  margin-bottom: 2rem;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.5rem;
`;

const IconBox = styled.div`
  width: 2.5rem;
  height: 2.5rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  background: rgba(128, 0, 0, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.maroon};
`;

const Title = styled.h1`
  font-size: ${({ theme }) => theme.fontClamp['3xl']};
  font-weight: 700;
  color: ${({ theme }) => theme.colors.foreground};
  margin: 0;
`;

const Subtitle = styled.p`
  font-size: ${({ theme }) => theme.fontClamp.sm};
  color: ${({ theme }) => theme.colors.mutedForeground};
  margin: 0 0 0 3.25rem;
  max-width: 44rem;
  line-height: 1.55;
`;

const CardGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
  @media (min-width: 500px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (min-width: 768px) {
    grid-template-columns: repeat(3, 1fr);
    gap: 1.25rem;
  }
  @media (min-width: 1100px) {
    grid-template-columns: repeat(4, 1fr);
  }
  @media (min-width: 1400px) {
    grid-template-columns: repeat(5, 1fr);
  }
`;

const GenreCard = styled(motion(Link))`
  display: block;
  text-decoration: none;
  color: inherit;
  border-radius: ${({ theme }) => theme.radiusSm};
  overflow: hidden;
  box-shadow: ${({ theme }) => theme.shadowCard};
  background: ${({ theme }) => theme.colors.card};
  transition: box-shadow 0.2s;
  &:hover {
    box-shadow: ${({ theme }) => theme.shadowCardHover};
  }
`;

const GenreVisual = styled.div`
  height: 100px;
  background: ${({ theme }) => theme.colors.muted};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.primary};
`;

const GenreBody = styled.div`
  padding: 1.1rem 1.25rem 1.25rem;
`;

const GenreName = styled.h2`
  margin: 0 0 0.35rem;
  font-size: ${({ theme }) => theme.fontClamp.lg};
  font-weight: 800;
  color: ${({ theme }) => theme.colors.foreground};
`;

const GenreDesc = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.fontClamp.xs};
  color: ${({ theme }) => theme.colors.mutedForeground};
  line-height: 1.45;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const GenreCta = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  margin-top: 0.5rem;
  font-size: ${({ theme }) => theme.fontClamp.xs};
  font-weight: 700;
  color: ${({ theme }) => theme.colors.crimson};
`;

const genreIcons = {
  action: Swords,
  adventure: Compass,
  drama: Heart,
  comedy: Laugh,
  'sci-fi': Rocket,
  horror: Ghost,
  thriller: Zap,
  romance: Heart,
  animation: Film,
  fantasy: Castle,
};

export default function CategoriesHub() {
  const dispatch = useDispatch();
  const { sitePublicLoaded, adsManagedEnabled } = useSiteAdsManaged();
  const adsStaticEnabled = useSelector(selectAdsStaticEnabled);
  const { items: genres, loading, error } = useSelector((s) => s.genres);
  const { pathname } = useLocation();
  const region = regionFromPathname(pathname);
  const { t } = useI18n();

  useEffect(() => {
    dispatch(fetchPublicGenres());
  }, [dispatch]);

  const { adDomain, adLocale, adDevice } = useFetchManagedPlacements('categories', CATEGORIES_AD_REQ);
  const managedCatBanner = useSelector((s) => selectAdPlacementFirst(s, 'categories', 'inline-banner'));
  const managedCatInline = useSelector((s) => selectAdPlacementFirst(s, 'categories', 'results-inline'));

  const catBannerAd = useMemo(
    () => managedPlacementToAd(managedCatBanner, pickStaticFallback(adsStaticEnabled, bannerAds[0])),
    [managedCatBanner, adsStaticEnabled],
  );
  const catInlineAd = useMemo(
    () => managedPlacementToAd(managedCatInline, pickStaticFallback(adsStaticEnabled, cardAds[1])),
    [managedCatInline, adsStaticEnabled],
  );

  useEffect(() => {
    if (!sitePublicLoaded || !adsManagedEnabled || !managedCatBanner?._id) return;
    const key = makeImpressionKey({
      campaign: managedCatBanner._id,
      page: 'categories',
      placement: 'inline-banner',
      device: adDevice,
      domain: adDomain,
    });
    if (shouldTrackImpression(key)) {
      dispatch(
        trackAdImpression({
          campaign: managedCatBanner._id,
          page: 'categories',
          placement: 'inline-banner',
          device: adDevice,
          domain: adDomain,
          locale: adLocale,
        }),
      );
    }
  }, [dispatch, managedCatBanner, adDevice, adDomain, adLocale, sitePublicLoaded, adsManagedEnabled]);

  useEffect(() => {
    if (!sitePublicLoaded || !adsManagedEnabled || !managedCatInline?._id) return;
    const key = makeImpressionKey({
      campaign: managedCatInline._id,
      page: 'categories',
      placement: 'results-inline',
      device: adDevice,
      domain: adDomain,
    });
    if (shouldTrackImpression(key)) {
      dispatch(
        trackAdImpression({
          campaign: managedCatInline._id,
          page: 'categories',
          placement: 'results-inline',
          device: adDevice,
          domain: adDomain,
          locale: adLocale,
        }),
      );
    }
  }, [dispatch, managedCatInline, adDevice, adDomain, adLocale, sitePublicLoaded, adsManagedEnabled]);

  const onCatBannerClick = useCallback(() => {
    if (!sitePublicLoaded || !adsManagedEnabled || !managedCatBanner?._id) return;
    dispatch(
      trackAdClick({
        campaign: managedCatBanner._id,
        page: 'categories',
        placement: 'inline-banner',
        device: adDevice,
        domain: adDomain,
        locale: adLocale,
      }),
    );
  }, [dispatch, managedCatBanner, adDevice, adDomain, adLocale, sitePublicLoaded, adsManagedEnabled]);

  const onCatInlineClick = useCallback(() => {
    if (!sitePublicLoaded || !adsManagedEnabled || !managedCatInline?._id) return;
    dispatch(
      trackAdClick({
        campaign: managedCatInline._id,
        page: 'categories',
        placement: 'results-inline',
        device: adDevice,
        domain: adDomain,
        locale: adLocale,
      }),
    );
  }, [dispatch, managedCatInline, adDevice, adDomain, adLocale, sitePublicLoaded, adsManagedEnabled]);

  return (
    <PageLayout>
      <Container style={{ paddingTop: '2.5rem', paddingBottom: '3rem' }}>
        <Header initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
          <HeaderRow>
            <IconBox>
              <LayoutGrid size={20} strokeWidth={2.5} />
            </IconBox>
            <Title>{t('categories.hub.title', 'Categories by genre')}</Title>
          </HeaderRow>
          <Subtitle>
            {t(
              'categories.hub.subtitle',
              'Browse movies, TV shows, and anime by genre. Open any title to see similar picks.',
            )}
          </Subtitle>
        </Header>

        <div style={{ margin: '1.5rem 0' }}>
          <StaticAdBanner ad={catBannerAd} variant="inline" pageKey="categories" onLinkClick={onCatBannerClick} />
        </div>

        <CardGrid>
          {loading && !genres.length ? (
            <PageLoading message={t('common.loading', 'Loading...')} />
          ) : null}
          {!loading && !genres.length ? (
            <p style={{ gridColumn: '1 / -1', color: 'var(--muted-foreground)' }}>
              {error || t('categories.hub.empty', 'No genres available yet.')}
            </p>
          ) : null}
          {genres.map((genre, i) => {
            const Icon = genreIcons[genre.slug] || Sparkles;
            return (
              <GenreCard
                key={genre.slug}
                to={categoriesGenrePath(genre.slug, region)}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.04 }}
                whileHover={{ y: -3 }}
              >
                <GenreVisual>
                  <Icon size={36} strokeWidth={2} />
                </GenreVisual>
                <GenreBody>
                  <GenreName>{genre.name}</GenreName>
                  <GenreDesc>{genre.desc}</GenreDesc>
                  <GenreCta>
                    {t('categories.hub.browseGenre', 'Browse')} {genre.name} <ArrowRight size={14} />
                  </GenreCta>
                </GenreBody>
              </GenreCard>
            );
          })}
        </CardGrid>
        <div style={{ marginTop: '2rem' }}>
          <AdInline ad={catInlineAd} pageKey="categories" onLinkClick={onCatInlineClick} />
        </div>
      </Container>
    </PageLayout>
  );
}
